import { useEffect, useRef } from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { type IOrderNotification } from '@/types/INotification';
import { getOrderToastSummary } from '@/utils/getOrderToastSummary';
import { ORDER_TOAST_LIFETIME_MS } from '@/constants/socketTimings';
import CrossIcon from '@/assets/icon/components/CrossIcon';
import { TOAST_VARIANTS, TOAST_TRANSITION } from '../../toastMotion';
import styles from './orderToastCard.module.scss';

interface IOrderToastCardProps {
  notification: IOrderNotification;
  onNavigate: (path: string) => void;
  onClose: (id: string) => void;
  autoClose?: boolean;
  // Внутри схлопнутого списка layout-анимация сплющивает карточки при большом
  // их числе — там её отключаем, оставляя только enter/exit.
  disableLayout?: boolean;
}

export const OrderToastCard: React.FC<IOrderToastCardProps> = ({
  notification,
  onNavigate,
  onClose,
  autoClose = true,
  disableLayout = false
}) => {
  const summary = getOrderToastSummary(notification.payload.data);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = (): void => {
    if (!autoClose) {
      return;
    }
    timerRef.current = setTimeout(() => {
      onClose(notification.id);
    }, ORDER_TOAST_LIFETIME_MS);
  };

  useEffect(() => {
    startTimer();
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigate = (): void => {
    clearTimer();
    onNavigate(summary.orderPath);
    onClose(notification.id);
  };

  const handleClose = (event: React.MouseEvent): void => {
    event.stopPropagation();
    clearTimer();
    onClose(notification.id);
  };

  return (
    <motion.div
      layout={!disableLayout}
      variants={TOAST_VARIANTS}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={TOAST_TRANSITION}
      className={cn(
        styles['order-toast-card'],
        styles['order-toast-card_type-order']
      )}
      role="button"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          handleNavigate();
        }
      }}
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
    >
      <button
        type="button"
        className={styles['order-toast-card__close']}
        onClick={handleClose}
        aria-label="Закрыть уведомление"
      >
        <CrossIcon width={16} height={16} color="currentColor" />
      </button>

      <div className={styles['order-toast-card__body']}>
        <img
          className={styles['order-toast-card__thumb']}
          src={summary.imageSrc}
          alt={summary.title}
          loading="lazy"
        />

        <div className={styles['order-toast-card__content']}>
          <span className={styles['order-toast-card__label']}>Новый заказ</span>
          <span className={styles['order-toast-card__title']}>
            {summary.title}
          </span>

          <div className={styles['order-toast-card__row']}>
            <span className={styles['order-toast-card__cost']}>
              {summary.totalCost}
            </span>
            <span
              className={cn(
                styles['order-toast-card__payment'],
                styles[`order-toast-card__payment_${summary.paymentColor}`]
              )}
            >
              {summary.paymentLabel}
            </span>
          </div>

          {summary.recipientName !== null && (
            <span className={styles['order-toast-card__recipient']}>
              {summary.recipientName}
            </span>
          )}

          {summary.firstOfferName !== null && (
            <span className={styles['order-toast-card__offer']}>
              {summary.firstOfferName}
              {summary.offersCount > 1 && (
                <span className={styles['order-toast-card__more']}>
                  {` +${summary.offersCount - 1}`}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};
